import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Image, Video, Plus, Trash2, Edit, ExternalLink, Loader2, Search, Filter
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminGallery = () => {
  const { getAuthAxios } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [galleryItems, setGalleryItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [newItem, setNewItem] = useState({
    event_id: "hyderabad-2024",
    media_type: "image",
    url: "",
    caption: "",
    thumbnail_url: ""
  });

  const events = [
    { id: "hyderabad-2024", name: "Hyderabad 2024 (1st Edition)" },
    { id: "bengaluru-2025", name: "Bengaluru 2025 (2nd Edition)" }
  ];

  useEffect(() => {
    fetchGallery();
  }, []);

  useEffect(() => {
    filterItems();
  }, [galleryItems, selectedEvent, searchTerm]);

  const fetchGallery = async () => {
    try {
      const authAxios = getAuthAxios();
      const response = await authAxios.get(`${API}/admin/gallery`);
      setGalleryItems(response.data.items || []);
    } catch (error) {
      toast.error("Failed to fetch gallery");
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    let items = [...galleryItems];
    
    if (selectedEvent !== "all") {
      items = items.filter(item => item.event_id === selectedEvent);
    }
    
    if (searchTerm) {
      items = items.filter(item => 
        item.caption?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.url?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredItems(items);
  };

  const handleAddItem = async () => {
    if (!newItem.url) {
      toast.error("Please enter a URL");
      return;
    }

    setSubmitting(true);
    try {
      const authAxios = getAuthAxios();
      await axios.post(`${API}/gallery`, newItem);
      toast.success("Media added successfully!");
      setNewItem({
        event_id: "hyderabad-2024",
        media_type: "image",
        url: "",
        caption: "",
        thumbnail_url: ""
      });
      setAddDialogOpen(false);
      fetchGallery();
    } catch (error) {
      toast.error("Failed to add media");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      const authAxios = getAuthAxios();
      await authAxios.delete(`${API}/gallery/${itemId}`);
      toast.success("Media deleted successfully!");
      fetchGallery();
    } catch (error) {
      toast.error("Failed to delete media");
    }
  };

  const images = filteredItems.filter(item => item.media_type === "image");
  const videos = filteredItems.filter(item => item.media_type === "video");

  return (
    <div data-testid="admin-gallery">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#0A0A0A] font-['Outfit']">Gallery Management</h1>
          <p className="text-[#4B5563]">Add and manage photos & videos for past events</p>
        </div>
        
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-media-btn">
              <Plus className="w-5 h-5 mr-2" />
              Add Media
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Media to Gallery</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Event *</Label>
                <Select 
                  value={newItem.event_id} 
                  onValueChange={(v) => setNewItem(prev => ({ ...prev, event_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map(event => (
                      <SelectItem key={event.id} value={event.id}>{event.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Media Type *</Label>
                <Select 
                  value={newItem.media_type} 
                  onValueChange={(v) => setNewItem(prev => ({ ...prev, media_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>{newItem.media_type === "image" ? "Image URL *" : "Video URL *"}</Label>
                <Input 
                  placeholder={newItem.media_type === "image" 
                    ? "https://example.com/image.jpg" 
                    : "https://youtube.com/watch?v=... or direct MP4 URL"
                  }
                  value={newItem.url}
                  onChange={(e) => setNewItem(prev => ({ ...prev, url: e.target.value }))}
                />
                {newItem.media_type === "image" ? (
                  <div className="text-xs space-y-1">
                    <p className="text-[#4B5563]">
                      <strong>✅ Use:</strong> Direct image URLs ending in .jpg, .png, .webp
                    </p>
                    <p className="text-red-600">
                      <strong>❌ Won't work:</strong> Google Drive folders, webpage links, or non-image URLs
                    </p>
                    <p className="text-blue-600">
                      💡 Right-click an image → "Copy Image Address" to get the direct URL
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-[#4B5563]">
                    Use YouTube, Vimeo links or direct MP4 URLs
                  </p>
                )}
              </div>
              
              {/* URL validation warning */}
              {newItem.url && newItem.media_type === "image" && !newItem.url.match(/\.(jpg|jpeg|png|gif|webp|svg)($|\?)/i) && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    ⚠️ This doesn't look like a direct image URL. Make sure it ends with .jpg, .png, .webp etc.
                    <br />
                    <span className="text-yellow-700">Google Drive folders and webpage links won't display as images.</span>
                  </p>
                </div>
              )}
              
              {newItem.media_type === "video" && (
                <div className="space-y-2">
                  <Label>Thumbnail URL (optional)</Label>
                  <Input 
                    placeholder="https://example.com/thumbnail.jpg"
                    value={newItem.thumbnail_url}
                    onChange={(e) => setNewItem(prev => ({ ...prev, thumbnail_url: e.target.value }))}
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Caption</Label>
                <Textarea 
                  placeholder="Describe this media..."
                  value={newItem.caption}
                  onChange={(e) => setNewItem(prev => ({ ...prev, caption: e.target.value }))}
                  rows={2}
                />
              </div>
              
              <Button 
                onClick={handleAddItem} 
                className="w-full btn-primary"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add to Gallery
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input 
            placeholder="Search by caption or URL..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={selectedEvent} onValueChange={setSelectedEvent}>
          <SelectTrigger className="w-full sm:w-64">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by event" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            {events.map(event => (
              <SelectItem key={event.id} value={event.id}>{event.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="flex gap-4 mb-6">
        <div className="flex items-center gap-2 px-4 py-2 bg-pink-50 rounded-lg">
          <Image className="w-4 h-4 text-pink-600" />
          <span className="text-sm font-medium text-pink-700">{images.length} Photos</span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg">
          <Video className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-700">{videos.length} Videos</span>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#FF3B30]" />
          <p className="mt-2 text-[#4B5563]">Loading gallery...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Image className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-[#0A0A0A] mb-2">No Media Found</h3>
            <p className="text-[#4B5563] mb-4">
              {searchTerm || selectedEvent !== "all" 
                ? "Try adjusting your filters"
                : "Start by adding photos and videos from your events"
              }
            </p>
            <Button onClick={() => setAddDialogOpen(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add Media
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="images">
          <TabsList className="mb-6">
            <TabsTrigger value="images" className="flex items-center gap-2">
              <Image className="w-4 h-4" />
              Photos ({images.length})
            </TabsTrigger>
            <TabsTrigger value="videos" className="flex items-center gap-2">
              <Video className="w-4 h-4" />
              Videos ({videos.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="images">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((item) => (
                <Card key={item.id} className="overflow-hidden group">
                  <div className="relative aspect-square">
                    <img 
                      src={item.url}
                      alt={item.caption || "Gallery image"}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <a 
                        href={item.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-2 bg-white rounded-full hover:bg-gray-100"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button 
                        onClick={() => handleDeleteItem(item.id)}
                        className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <CardContent className="p-3">
                    <p className="text-xs text-[#FF3B30] font-medium mb-1">
                      {events.find(e => e.id === item.event_id)?.name?.split(" (")[0]}
                    </p>
                    <p className="text-sm text-[#4B5563] line-clamp-2">
                      {item.caption || "No caption"}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="videos">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.map((item) => (
                <Card key={item.id} className="overflow-hidden">
                  <div className="relative aspect-video bg-[#111827]">
                    {item.thumbnail_url ? (
                      <img 
                        src={item.thumbnail_url}
                        alt={item.caption || "Video thumbnail"}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="w-12 h-12 text-gray-600" />
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center gap-2">
                      <a 
                        href={item.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-14 h-14 bg-[#FF3B30] rounded-full flex items-center justify-center hover:bg-[#FF1C0F] transition-colors"
                      >
                        <Video className="w-6 h-6 text-white" />
                      </a>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs text-[#FF3B30] font-medium mb-1">
                          {events.find(e => e.id === item.event_id)?.name?.split(" (")[0]}
                        </p>
                        <p className="text-sm font-medium text-[#0A0A0A]">
                          {item.caption || "Untitled Video"}
                        </p>
                      </div>
                      <button 
                        onClick={() => handleDeleteItem(item.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default AdminGallery;
